import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';

import Header from '../../components/Header';
import { tokens } from '../../theme';

const articles = [
  {
    id: 'daily-lis-workflow',
    icon: <MenuBookOutlinedIcon />,
    category: 'Getting Started',
    title: 'Daily LIS Workflow: From Login to Completed Report',
    summary:
      'Teach staff the normal flow of work: sign in, choose the right area, enter or review results, generate a report, and know when to ask for help.',
    audience: 'Lab staff, reviewers, and new LIS users',
    readTime: '8 min lesson',
    objective: 'By the end, a user should know the basic path for completing routine work in the LIS without changing admin settings.',
    outcomes: [
      'Identify the main daily-work areas of the LIS.',
      'Know whether to start from Lab Results or Department Reports.',
      'Understand what to check before generating or sharing a report.',
      'Recognize when a missing page or button is probably a permissions issue.',
    ],
    steps: [
      {
        title: 'Start with your own login',
        body:
          'Each staff member should use their own account. Shared logins make it hard to confirm who entered or changed information and can expose more access than a person needs.',
      },
      {
        title: 'Choose the correct workflow',
        body:
          'Use Lab Results when your organization is working in the standard result workflow. Use Department Reports when your organization has department-specific manual workflows, such as a custom toxicology, urinalysis, blood, or other configured department.',
      },
      {
        title: 'Confirm the organization and department context',
        body:
          'Before entering data, confirm you are working in the right organization and, when applicable, the right department. If either one looks wrong, stop and contact an admin before continuing.',
      },
      {
        title: 'Enter or review the result carefully',
        body:
          'Follow your organization’s SOP for result entry. Check patient/client identifiers, collection details, analyte names, values, units, flags, and any positive/negative or detected/not-detected labels before moving forward.',
      },
      {
        title: 'Generate and review the report',
        body:
          'Open the generated report/PDF and review it before sending, saving, or sharing it. Confirm the layout, organization details, footer/disclaimer language, and result values all look correct.',
      },
    ],
    keyTakeaways: [
      'Daily users should not need to change organization, role, department, or report-template settings.',
      'If something is missing, it may be controlled by your role rather than an app problem.',
      'Always review a generated report before using it outside the LIS.',
    ],
    caution:
      'Do not continue entering or sharing results if the organization, patient/client, department, or report template appears incorrect.',
    practice:
      'Practice by opening the correct daily workflow for your role, locating a department or result area, and identifying which fields must be checked before a report is generated.',
  },
  {
    id: 'departments-and-analytes',
    icon: <ScienceOutlinedIcon />,
    category: 'Lab Configuration',
    title: 'Departments and Analytes: How Your Test Menu Is Organized',
    summary:
      'Explain what departments and analytes are, how they affect manual result entry, and why admins should configure only what the organization actually uses.',
    audience: 'Organization admins, supervisors, and trained lab staff',
    readTime: '10 min lesson',
    objective: 'Teach users how department setup controls the test menu and the manual reporting experience.',
    outcomes: [
      'Understand the difference between a department and an analyte.',
      'Know why departments are configured per organization.',
      'Understand how imported analytes appear in manual result workflows.',
      'Recognize the risk of deleting departments or analytes.',
    ],
    steps: [
      {
        title: 'Understand departments',
        body:
          'A department is a group of related testing or reporting workflows. Examples may include toxicology, urinalysis, blood testing, molecular testing, or any custom workflow your organization needs. Departments keep result entry focused instead of placing every possible test on one screen.',
      },
      {
        title: 'Understand analytes',
        body:
          'An analyte is an individual result item that appears inside a department. Depending on the workflow, an analyte might be a drug class, medication, organism, measurement, marker, or other reportable item.',
      },
      {
        title: 'Set up only the departments you use',
        body:
          'The LIS is designed so organizations do not have to carry unused default departments. Admins should create the departments their team actually needs and keep inactive or unused workflows out of daily staff view.',
      },
      {
        title: 'Import or manage analytes for each department',
        body:
          'After a department exists, analytes can be added or imported so staff can select and report the correct items. Keeping analyte names, units, cutoff labels, and sort order clean makes reporting faster and less error-prone.',
      },
      {
        title: 'Review before removing anything',
        body:
          'Removing a department or analyte can affect what appears in manual entry and may affect related result records. Before removing anything, confirm the organization, department, and item name with your internal process.',
      },
    ],
    keyTakeaways: [
      'Departments organize workflows; analytes are the items reported inside those workflows.',
      'A clean department/analyte setup makes manual result entry easier for staff.',
      'Deletion should be treated as an admin action, not routine daily work.',
    ],
    caution:
      'If you are not sure whether an analyte is still used in active reporting, do not delete it. Rename, deactivate, or ask support/admin first based on your organization’s SOP.',
    practice:
      'Pick one department your organization uses. List three analytes that belong in it and one analyte that should not belong there. This helps confirm staff understand the test-menu structure.',
  },
  {
    id: 'manual-entry-csv-and-interfaces',
    icon: <UploadFileOutlinedIcon />,
    category: 'Result Entry',
    title: 'Manual Entry, CSV Imports, and Machine Interfaces',
    summary:
      'Teach the difference between starting manually, using CSV templates, and requesting a more automated instrument/interface workflow.',
    audience: 'Admins and lab operators responsible for department setup',
    readTime: '9 min lesson',
    objective: 'Help teams choose the right workflow maturity level and avoid waiting on automation when manual reporting can start safely.',
    outcomes: [
      'Know what manual-first reporting means.',
      'Understand what CSV templates are for.',
      'Know what an interface request means and does not mean.',
      'Use the correct workflow while automation is being configured.',
    ],
    steps: [
      {
        title: 'Start with manual-first reporting',
        body:
          'Manual-first reporting means your organization can begin using a configured department before an instrument interface is complete. Admins set up the department and analytes, then staff can manually enter results according to your SOP.',
      },
      {
        title: 'Use CSV templates to standardize analytes',
        body:
          'CSV templates reduce typing errors by giving admins a consistent format for analyte setup. Download the template from the department, fill in the required columns, review the file, and import it back into the LIS.',
      },
      {
        title: 'Review imported analytes before go-live',
        body:
          'After importing, check the analyte list for spelling, duplicates, units, cutoff labels, display order, and any missing items. A small review upfront prevents staff confusion during result entry.',
      },
      {
        title: 'Understand interface requests',
        body:
          'A CSV or machine-interface request tells support that your organization wants automation. It does not mean the instrument is already connected. Until support confirms the interface is live, continue using the manual workflow.',
      },
      {
        title: 'Move from manual to automated carefully',
        body:
          'When an automated workflow becomes available, compare sample outputs against manual expectations. Confirm result names, values, units, and flags before relying on automated data in production.',
      },
    ],
    keyTakeaways: [
      'Manual-first lets teams start safely without waiting for integrations.',
      'CSV templates are for analyte setup, not a guarantee that a machine interface is live.',
      'Any automation should be validated before it replaces a manual workflow.',
    ],
    caution:
      'Do not assume a requested interface is active until your admin or support contact confirms testing is complete.',
    practice:
      'Download a department analyte template, identify the required columns, and explain how you would check the imported list before staff begin using it.',
  },
  {
    id: 'reports-and-pdfs',
    icon: <PictureAsPdfOutlinedIcon />,
    category: 'Reports',
    title: 'Reports and PDFs: What to Review Before Sharing Results',
    summary:
      'Teach users how generated reports are assembled and what must be reviewed before a report leaves the LIS workflow.',
    audience: 'Report reviewers, admins, and staff who generate PDFs',
    readTime: '8 min lesson',
    objective: 'Make report review a repeatable habit so users catch incorrect settings or values before sharing results.',
    outcomes: [
      'Understand what report settings control.',
      'Know the difference between result data and report presentation.',
      'Use a report review checklist before sharing PDFs.',
      'Know when to escalate a report formatting issue.',
    ],
    steps: [
      {
        title: 'Know what report settings control',
        body:
          'Report settings can control layout, footer text, disclaimers, optional analyte catalog information, and other customer-facing presentation details. These settings shape the report but do not replace careful result review.',
      },
      {
        title: 'Review identifiers first',
        body:
          'Before focusing on result values, confirm the organization, patient/client identifiers, accession or specimen information, collection date, and report date are correct according to your workflow.',
      },
      {
        title: 'Review result content',
        body:
          'Check analyte names, values, units, flags, positive/negative labels, detected/not-detected labels, and any comments. If an item appears in the wrong department or with the wrong label, stop and ask an admin to review setup.',
      },
      {
        title: 'Review presentation and compliance text',
        body:
          'Make sure the report is readable and the footer/disclaimer language is appropriate for your organization. If your organization requires specific language, confirm it is present before distributing the report.',
      },
      {
        title: 'Escalate formatting issues',
        body:
          'If a PDF is cut off, missing text, showing the wrong org information, or using an unexpected layout, do not keep generating reports with the same issue. Contact an admin or support contact.',
      },
    ],
    keyTakeaways: [
      'A generated PDF still needs human review before it is shared.',
      'Report settings affect presentation; result review remains a staff responsibility.',
      'Formatting or disclaimer problems should be escalated before reports are used externally.',
    ],
    caution:
      'Never send or file a report that has incorrect identifiers, wrong organization details, missing disclaimers, or visibly incorrect result formatting.',
    practice:
      'Use a sample report and perform a 60-second review: identifiers, result values, labels/units, footer/disclaimer, and overall readability.',
  },
  {
    id: 'roles-and-access',
    icon: <PeopleAltOutlinedIcon />,
    category: 'Account Access',
    title: 'Roles and Access: Why Users See Different Screens',
    summary:
      'Teach staff why roles exist, what read-only access means, and how organization boundaries protect customer data.',
    audience: 'All LIS users and organization admins',
    readTime: '7 min lesson',
    objective: 'Help users understand that different access levels are intentional safeguards, not necessarily app errors.',
    outcomes: [
      'Understand why user roles exist.',
      'Know the difference between normal users, readers, admins, and platform support roles.',
      'Recognize why a user may not see an admin page or remove button.',
      'Know what to do if access looks too broad or too limited.',
    ],
    steps: [
      {
        title: 'Understand least-privilege access',
        body:
          'The LIS should give each person the access they need for their job and no more. This protects patient, customer, and organization information while reducing accidental changes.',
      },
      {
        title: 'Know common access patterns',
        body:
          'A daily LIS user typically works with results and reports. A reader can view information without changing it. An organization admin can manage settings for their own organization. Platform support may have broader visibility when helping multiple organizations.',
      },
      {
        title: 'Expect buttons to change by role',
        body:
          'A read-only user may see information but not see buttons to create, import, edit, or remove records. That is expected. If a button is missing and you believe you need it, ask your organization admin to review your role.',
      },
      {
        title: 'Respect organization boundaries',
        body:
          'Most customer users should only see their assigned organization. If you can see another organization’s users, departments, or reports unexpectedly, stop and report it.',
      },
      {
        title: 'Request changes through the right person',
        body:
          'Role changes should go through your organization admin or support contact. Do not share another user’s login to work around a missing permission.',
      },
    ],
    keyTakeaways: [
      'Different screens and buttons by role are a security feature.',
      'Read-only access is useful for supervision and review without edit risk.',
      'Unexpected cross-organization access should be reported immediately.',
    ],
    caution:
      'Never borrow another user’s account to access a feature. Ask for your role to be reviewed instead.',
    practice:
      'Look at your sidebar and identify which areas you can access. Ask: “Does this match my job duties?” If not, notify your admin.',
  },
  {
    id: 'data-safety-basics',
    icon: <SecurityOutlinedIcon />,
    category: 'Security',
    title: 'Data Safety Basics for LIS Users',
    summary:
      'Teach practical habits that protect lab, patient, and organization data during daily LIS use.',
    audience: 'All LIS users',
    readTime: '7 min lesson',
    objective: 'Give users simple, repeatable habits for safer data handling inside the LIS.',
    outcomes: [
      'Know why individual accounts matter.',
      'Recognize sensitive LIS information.',
      'Use caution before changing configuration.',
      'Know when to report unusual access or data issues.',
    ],
    steps: [
      {
        title: 'Use individual accounts',
        body:
          'Individual accounts help your organization apply the right permissions and maintain accountability. Shared accounts make it harder to know who entered a result, changed a setting, or generated a report.',
      },
      {
        title: 'Treat LIS data as sensitive',
        body:
          'Patient/client identifiers, specimens, lab results, organization settings, report templates, and user records should all be handled carefully. Do not copy information into unsecured tools or messages.',
      },
      {
        title: 'Slow down before configuration changes',
        body:
          'Department, analyte, role, user, and report settings can affect live workflows. If a change could affect other staff or reports, confirm it through your internal process first.',
      },
      {
        title: 'Check your context',
        body:
          'Before entering data or changing settings, confirm the organization and department context. Many mistakes start with being on the wrong org, department, or record.',
      },
      {
        title: 'Report unusual access or incorrect data',
        body:
          'If you can see information you should not see, or if data appears wrong, stop and report it. Do not try to fix security or data-boundary issues by guessing.',
      },
    ],
    keyTakeaways: [
      'Use your own login and do not share credentials.',
      'Verify organization, department, and record context before acting.',
      'Report suspicious access or incorrect data instead of working around it.',
    ],
    caution:
      'Do not export, screenshot, or share sensitive LIS information outside approved organization channels.',
    practice:
      'Before your next result or admin change, say out loud or mentally confirm: correct user, correct organization, correct department, correct record, correct action.',
  },
];

function PageShell({ children }) {
  return (
    <Box
      sx={{
        flex: '1 1 auto',
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        p: 3,
        pb: 6,
      }}
    >
      {children}
    </Box>
  );
}

function BulletList({ items }) {
  return (
    <List dense sx={{ py: 0 }}>
      {items.map((item) => (
        <ListItem key={item} sx={{ alignItems: 'flex-start', px: 0 }}>
          <ListItemIcon sx={{ minWidth: 34, mt: 0.3 }}>
            <CheckCircleOutlineIcon color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={item} primaryTypographyProps={{ color: 'text.secondary' }} />
        </ListItem>
      ))}
    </List>
  );
}

function ArticleCard({ article, colors, onOpen }) {
  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <CardContent sx={{ flex: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: '14px',
              display: 'grid',
              placeItems: 'center',
              color: 'primary.main',
              backgroundColor: `${colors.blueAccent[700]}22`,
            }}
          >
            {article.icon}
          </Box>
          <Box>
            <Chip size="small" label={article.category} variant="outlined" />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {article.readTime}
            </Typography>
          </Box>
        </Stack>

        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {article.title}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {article.summary}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
          What this teaches
        </Typography>
        <BulletList items={article.outcomes.slice(0, 3)} />
      </CardContent>
      <CardActions sx={{ px: 2, pb: 2 }}>
        <Button
          onClick={() => onOpen(article.id)}
          variant="contained"
          endIcon={<ArrowForwardIcon />}
        >
          Open lesson
        </Button>
      </CardActions>
    </Card>
  );
}

function EducationIndex({ articles: articleList }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  return (
    <PageShell>
      <Header
        title="Education Hub"
        subtitle="In-depth customer-facing LIS lessons for lab teams, admins, and reviewers."
      />

      <Card
        sx={{
          mt: 2,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          background:
            theme.palette.mode === 'dark'
              ? `linear-gradient(135deg, ${colors.grey[800]} 0%, ${colors.grey[900]} 70%)`
              : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${colors.grey[100]} 70%)`,
        }}
      >
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
                <Chip color="primary" label="Education Library" />
                <Chip variant="outlined" label="Step-by-step lessons" />
                <Chip variant="outlined" label="Customer Resources" />
              </Stack>
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
                Learn the LIS by completing real workflow lessons.
              </Typography>
              <Typography color="text.secondary" sx={{ maxWidth: 860, fontSize: 16 }}>
                These articles are built to teach specific LIS functions, not just define terms. Open a lesson to see
                what the workflow does, when to use it, step-by-step guidance, cautions, and a short practice activity.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'transparent' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Start here
                </Typography>
                <Stack spacing={1}>
                  {articleList.slice(0, 3).map((article) => (
                    <Button
                      key={article.id}
                      onClick={() => navigate(`/education/${article.id}`)}
                      variant="outlined"
                      size="small"
                      sx={{ justifyContent: 'flex-start' }}
                    >
                      {article.title}
                    </Button>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2} sx={{ mt: 1 }}>
        {articleList.map((article) => (
          <Grid item xs={12} md={6} key={article.id}>
            <ArticleCard article={article} colors={colors} onOpen={(id) => navigate(`/education/${id}`)} />
          </Grid>
        ))}
      </Grid>

      <Card sx={{ mt: 2, border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Need a topic added?
          </Typography>
          <Typography color="text.secondary">
            Ask your organization admin or support contact to request another customer education lesson. Good topics
            include new department workflows, report interpretation, CSV formatting, instrument interfaces, and role-based access.
          </Typography>
        </CardContent>
      </Card>
    </PageShell>
  );
}

function ArticlePage({ article, articleList }) {
  const navigate = useNavigate();
  const currentIndex = articleList.findIndex((item) => item.id === article.id);
  const nextArticle = articleList[(currentIndex + 1) % articleList.length];

  return (
    <PageShell>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/education')} variant="outlined">
          Education Hub
        </Button>
        <Chip label={article.category} color="primary" variant="outlined" />
        <Chip label={article.readTime} variant="outlined" />
      </Stack>

      <Card sx={{ border: '1px solid', borderColor: 'divider', mb: 2 }}>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
            <Box
              sx={{
                width: 58,
                height: 58,
                borderRadius: '16px',
                display: 'grid',
                placeItems: 'center',
                color: 'primary.main',
                backgroundColor: 'action.hover',
              }}
            >
              {article.icon}
            </Box>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
                {article.title}
              </Typography>
              <Typography color="text.secondary" sx={{ maxWidth: 920, fontSize: 16 }}>
                {article.summary}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Stack spacing={2}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  Lesson objective
                </Typography>
                <Typography color="text.secondary">{article.objective}</Typography>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                  Step-by-step workflow
                </Typography>
                <Stack spacing={2}>
                  {article.steps.map((step, index) => (
                    <Box key={step.title}>
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Chip label={index + 1} color="primary" size="small" sx={{ mt: 0.25 }} />
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {step.title}
                          </Typography>
                          <Typography color="text.secondary">{step.body}</Typography>
                        </Box>
                      </Stack>
                      {index < article.steps.length - 1 ? <Divider sx={{ mt: 2 }} /> : null}
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>

            <Alert severity="warning" variant="outlined">
              <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Caution</Typography>
              {article.caution}
            </Alert>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  Practice activity
                </Typography>
                <Typography color="text.secondary">{article.practice}</Typography>
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack spacing={2} sx={{ position: { md: 'sticky' }, top: 16 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Who this is for
                </Typography>
                <Typography color="text.secondary">{article.audience}</Typography>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  You should be able to
                </Typography>
                <BulletList items={article.outcomes} />
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Key takeaways
                </Typography>
                <BulletList items={article.keyTakeaways} />
              </CardContent>
            </Card>

            <Button
              onClick={() => navigate(`/education/${nextArticle.id}`)}
              variant="contained"
              endIcon={<ArrowForwardIcon />}
            >
              Next lesson
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </PageShell>
  );
}

export default function Education() {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const article = articles.find((item) => item.id === articleId);

  if (articleId && !article) {
    return (
      <PageShell>
        <Header title="Education Lesson Not Found" subtitle="The requested LIS lesson could not be found." />
        <Button onClick={() => navigate('/education')} variant="contained" startIcon={<ArrowBackIcon />}>
          Back to Education Hub
        </Button>
      </PageShell>
    );
  }

  if (article) return <ArticlePage article={article} articleList={articles} />;

  return <EducationIndex articles={articles} />;
}
